import React from "react";
import { formatDate } from "utils/helperFunctions";

const CATEGORY_LABELS = {
  media: "Media",
  postProduction: "Post Production",
  print: "Print",
  inspection: "Inspection",
  meeting: "Meeting",
  milestone: "Milestone",
};

const CATEGORY_COLORS = {
  media: "bg-blue-100 text-blue-800",
  postProduction: "bg-purple-100 text-purple-800",
  print: "bg-yellow-100 text-yellow-800",
  inspection: "bg-green-100 text-green-800",
  meeting: "bg-orange-100 text-orange-800",
  milestone: "bg-red-100 text-red-800",
};

const SERVICE_LABELS = {
  photo: "Photo",
  video: "Video",
  floorplan: "Floorplan",
  floorplanRedraw: "Floorplan Redraw",
  auction: "Auction",
};

const formatDuration = (minutes) => {
  if (!minutes) return null;
  const hrs = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hrs === 0) return `${mins} min`;
  if (mins === 0) return `${hrs} hr`;
  return `${hrs}h ${mins}m`;
};

const BoolBadge = ({ value, trueLabel = "Yes", falseLabel = "No" }) => (
  <span
    className={`px-2 py-1 rounded-full text-xs font-medium ${
      value ? "bg-green-100 text-green-800" : "bg-lightgray text-darkgray"
    }`}
  >
    {value ? trueLabel : falseLabel}
  </span>
);

export default function EventTypeView({ eventType }) {
  if (!eventType) return null;

  const cat = eventType.category;

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="bg-lightgray p-6 rounded-lg mb-6">
        <div className="flex justify-between items-start">
          <div>
            <div className="text-base font-semibold text-black mb-2 flex items-center gap-2">
              {eventType.name}
              <span
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  CATEGORY_COLORS[cat] || "bg-gray-100 text-gray-800"
                }`}
              >
                {CATEGORY_LABELS[cat] || cat}
              </span>
            </div>
            <p className="text-darkgray font-mono text-sm">{eventType.key}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* General Information */}
        <div>
          <div className="font-medium text-black mb-3">General Information</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between">
              <span className="text-darkgray">Key:</span>
              <span className="text-black font-mono">{eventType.key}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-darkgray">Name:</span>
              <span className="text-black font-medium">{eventType.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-darkgray">Category:</span>
              <span
                className={`px-2 py-1 rounded-full text-xs font-medium ${
                  CATEGORY_COLORS[cat] || "bg-gray-100 text-gray-800"
                }`}
              >
                {CATEGORY_LABELS[cat] || cat}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Requires Duration:</span>
              <BoolBadge
                value={eventType.requiresDuration}
                trueLabel="Yes"
                falseLabel="No"
              />
            </div>
            {eventType.requiresDuration && (
              <div className="flex justify-between">
                <span className="text-darkgray">Default Duration:</span>
                <span className="text-black">
                  {formatDuration(eventType.defaultDurationMinutes) || "Not set"}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-darkgray">Created:</span>
              <span className="text-black">
                {formatDate(eventType.createdAt)}
              </span>
            </div>
          </div>
        </div>

        {/* Contractor & Agent Requirements */}
        <div>
          <div className="font-medium text-black mb-3">
            Contractor & Agent Requirements
          </div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Requires Contractor:</span>
              <BoolBadge
                value={eventType.requiresContractor}
                trueLabel="Required"
                falseLabel="Not Required"
              />
            </div>
            {eventType.requiresContractor && eventType.contractorService && (
              <div className="flex justify-between">
                <span className="text-darkgray">Service Type:</span>
                <span className="text-black font-medium">
                  {SERVICE_LABELS[eventType.contractorService] ||
                    eventType.contractorService}
                </span>
              </div>
            )}
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Requires Agent:</span>
              <BoolBadge
                value={eventType.requiresAgent}
                trueLabel="Required"
                falseLabel="Not Required"
              />
            </div>
          </div>
        </div>

        {/* Scheduling Options */}
        <div>
          <div className="font-medium text-black mb-3">Scheduling Options</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Allow Multiple:</span>
              <BoolBadge
                value={eventType.allowMultiple}
                trueLabel="Allowed"
                falseLabel="Single Only"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Allow Custom Time:</span>
              <BoolBadge
                value={eventType.allowCustomTime}
                trueLabel="Allowed"
                falseLabel="Fixed Slots"
              />
            </div>
          </div>
        </div>

        {/* Flags */}
        <div>
          <div className="font-medium text-black mb-3">Additional Flags</div>
          <div className="bg-lightgray rounded-lg p-4 space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Portal Upload:</span>
              <BoolBadge
                value={eventType.requiresPortalUpload}
                trueLabel="Required"
                falseLabel="Not Required"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="text-darkgray">Campaign Update:</span>
              <BoolBadge
                value={eventType.requiresCampaignUpdate}
                trueLabel="Required"
                falseLabel="Not Required"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}